import { useState, useMemo } from "react";
import {
  ShoppingBag,
  Stethoscope,
  ScanLine,
  Check,
  ChevronDown,
  Lock,
  ArrowUpRight,
  Briefcase,
  Leaf,
} from "lucide-react";

/* -------------------- Data -------------------- */

const COUNTRIES = [
  { code: "US", name: "United States", currency: "USD", flag: "🇺🇸", active: true },
  { code: "CA", name: "Canada", currency: "CAD", flag: "🇨🇦", active: true },
  { code: "EC", name: "Ecuador", currency: "USD", flag: "🇪🇨", active: false },
  { code: "JP", name: "Japan", currency: "JPY", flag: "🇯🇵", active: false },
  { code: "GB", name: "Great Britain", currency: "GBP", flag: "🇬🇧", active: false },
  { code: "WA", name: "West Africa", currency: "Multi", flag: "🌍", active: false },
];

const STORES_BY_COUNTRY = {
  US: ["Whole Foods", "Sprouts", "Trader Joe's", "Target", "Kroger", "Costco", "Walmart", "Michaels", "Pro service directories"],
  CA: ["Loblaws", "Sobeys", "Metro", "Whole Foods CA", "Walmart Canada", "Costco Canada", "Indigo", "Canadian Tire", "Pro service directories"],
};

const PRODUCT_CATEGORIES = [
  { id: "beauty", label: "Beauty", count: "1.2K+" },
  { id: "grocery", label: "Grocery", count: "4.8K+" },
  { id: "outdoor", label: "Outdoor", count: "640+" },
  { id: "wellness", label: "Wellness", count: "920+" },
];

const SERVICE_VERTICALS = [
  { code: "01", label: "Health / Tech", icon: Briefcase, body: "SaaS, digital health innovators, and tech infrastructure." },
  { code: "02", label: "Climate / Green", icon: Leaf, body: "Sustainability consultants, eco-services, and green solutions." },
  { code: "03", label: "Health / Medical", icon: Stethoscope, body: "Female-founded clinical practices, biotech, and medical providers." },
];

/* -------------------- Country dropdown -------------------- */

function CountryDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = COUNTRIES.find((c) => c.code === value) || COUNTRIES[0];
  return (
    <div className="relative" data-testid="country-dropdown">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="country-dropdown-trigger"
        className="inline-flex items-center gap-3 border-2 border-line bg-white px-5 py-3 font-sans font-semibold text-sm uppercase tracking-wider text-ink hover:border-link transition-colors"
      >
        <span className="text-xl leading-none" aria-hidden>{current.flag}</span>
        <span>{current.code} <span className="text-ink-soft font-normal tabular-nums">({current.currency})</span></span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <ul className="absolute right-0 mt-2 w-72 border-2 border-line bg-white z-40" role="listbox">
            <li className="px-4 py-2 border-b border-line font-sans font-semibold text-xs uppercase tracking-widest text-link">
              Global Roadmap
            </li>
            {COUNTRIES.map((c) => {
              const isActive = c.active;
              const isSelected = c.code === value;
              return (
                <li key={c.code}>
                  <button
                    onClick={() => { if (!isActive) return; onChange(c.code); setOpen(false); }}
                    disabled={!isActive}
                    data-testid={`country-option-${c.code}`}
                    className={`w-full flex items-center justify-between px-4 py-3 border-b border-line last:border-b-0 text-left transition-colors ${
                      isActive ? "hover:border-link text-ink" : "text-ink-soft cursor-not-allowed opacity-60"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span className="text-base leading-none" aria-hidden>{c.flag}</span>
                      <span className="font-sans font-semibold text-sm">{c.name}</span>
                    </span>
                    {isActive ? (
                      <span className="font-sans text-xs text-ink-soft tabular-nums">
                        {c.currency}
                        {isSelected && <Check className="w-3.5 h-3.5 ml-2 inline text-brand-orange" />}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 font-sans font-semibold text-[9px] uppercase tracking-widest border border-line px-2 py-0.5">
                        <Lock className="w-2.5 h-2.5" /> Soon
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

/* -------------------- Products panel (with Beauty/Grocery/Outdoor/Wellness toggle) -------------------- */

function ProductsPanel({ country }) {
  const [activeCat, setActiveCat] = useState("beauty");
  const stores = STORES_BY_COUNTRY[country] || STORES_BY_COUNTRY.US;
  const currency = country === "CA" ? "CAD" : "USD";

  return (
    <div data-testid="products-panel" className="space-y-10">
      <div>
        <div className="flex items-baseline justify-between border-b border-line pb-3 mb-6">
          <span className="font-sans font-semibold text-sm uppercase tracking-widest text-link">
            Product categories (live)
          </span>
          <span className="font-sans font-semibold text-xs uppercase tracking-widest text-ink-soft">
            Verified · women-owned
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {PRODUCT_CATEGORIES.map((c) => {
            const isActive = c.id === activeCat;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                data-testid={`category-${c.id}`}
                className={`flex flex-col items-start gap-1 p-4 border-2 transition-colors duration-150 ${
                  isActive
                    ? "border-brand-orange bg-brand-orange text-white"
                    : "border-line bg-white text-ink hover:border-link"
                }`}
              >
                <span className={`font-sans font-semibold text-lg ${isActive ? "text-white" : "text-ink"}`}>
                  {c.label}
                </span>
                <span className={`font-sans text-xs tabular-nums ${isActive ? "text-white/85" : "text-ink-soft"}`}>
                  {c.count} SKUs
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between border-b border-line pb-3 mb-6">
          <span className="font-sans font-semibold text-sm uppercase tracking-widest text-link">
            Stores & platforms supported · {country}
          </span>
          <span className="font-sans font-semibold text-xs uppercase tracking-widest text-brand-orange tabular-nums">
            {currency}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-px bg-line border border-line">
          {stores.map((s) => (
            <div key={s} data-testid={`store-${s.replace(/\W+/g, "-").toLowerCase()}`} className="bg-white px-3 py-4 flex items-center justify-center text-center">
              <span className="font-sans font-semibold text-sm tracking-tight text-ink leading-tight">{s}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Services panel -------------------- */

function ServicesPanel() {
  return (
    <div data-testid="services-panel" className="space-y-10">
      <div>
        <div className="flex items-baseline justify-between border-b border-line pb-3 mb-6">
          <span className="font-sans font-semibold text-sm uppercase tracking-widest text-link">
            Services Directory (live)
          </span>
          <span className="font-sans font-semibold text-xs uppercase tracking-widest text-ink-soft">
            Verified · women-led
          </span>
        </div>
        <div className="space-y-3">
          {SERVICE_VERTICALS.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.code} data-testid={`service-${v.code}`} className="group border-2 border-line p-5 hover:border-link transition-colors duration-150">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 border border-line text-link">
                      <Icon className="w-5 h-5" />
                    </span>
                    <h4 className="font-display font-bold text-ink text-xl tracking-tight leading-tight">
                      {v.label}
                    </h4>
                  </div>
                  <span className="font-sans text-xs uppercase tracking-widest text-ink-soft pt-1 tabular-nums">{v.code}</span>
                </div>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed pl-[52px]">{v.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Section -------------------- */

const TABS = [
  { id: "products", label: "Products", icon: ShoppingBag },
  { id: "services", label: "Services", icon: Stethoscope },
];

export default function ShowcaseTabs() {
  const [tab, setTab] = useState("products");
  const [country, setCountry] = useState("US");

  const headline = useMemo(() => {
    if (tab === "products") {
      return (
        <>Two products, one ecosystem. <span className="text-brand-orange">Both live.</span></>
      );
    }
    return (
      <>Search. Discover. <span className="text-brand-orange">Book.</span></>
    );
  }, [tab]);

  return (
    <section
      id="showcase"
      data-testid="showcase-section"
      className="relative border-b border-line bg-white"
    >
      <div className="w-full max-w-none px-4 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="font-sans font-semibold text-sm uppercase tracking-widest text-link block mb-3">
              03 / MVP & Services Showcase
            </span>
            <h2
              data-testid="showcase-headline"
              className="font-display font-bold tracking-tight text-ink text-[1.75rem] sm:text-[2rem] lg:text-[2.25rem] leading-[1.2] max-w-3xl"
            >
              {headline}
            </h2>
          </div>

          <div className={`transition-opacity duration-200 ${tab === "products" ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
            <span className="font-sans font-semibold text-xs uppercase tracking-widest text-ink-soft block mb-2 md:text-right">
              Global Roadmap
            </span>
            <CountryDropdown value={country} onChange={setCountry} />
          </div>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Showcase tabs"
          className="inline-flex border-2 border-line bg-white mb-10"
          data-testid="showcase-tabs"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.id === tab;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`inline-flex items-center gap-2.5 px-6 py-3.5 font-sans font-semibold text-sm uppercase tracking-widest border-r-2 border-line last:border-r-0 transition-colors duration-150 ${
                  isActive
                    ? "bg-brand-orange text-white"
                    : "bg-white text-ink hover:border-link"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Active panel */}
        <div className="mt-2">
          {tab === "products" ? <ProductsPanel country={country} /> : <ServicesPanel />}
        </div>
      </div>
    </section>
  );
}
